import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { pinMine, todayIso, validatePseudo, type LeaderboardApi, type LeaderboardRow } from '../../../net/leaderboard';
import { DailyButton } from '../DailyButton';
import type { DailyChoice } from '../dailySeed';
import { useEscape } from '../useEscape';
import { useInitialFocus } from '../useInitialFocus';

type Tab = 'daily' | 'global';
const TABS: { id: Tab; label: string }[] = [{ id: 'daily', label: 'Aujourd\'hui' }, { id: 'global', label: 'Global' }];
const OUTCOME: Record<LeaderboardRow['outcome'], string> = { victory: 'Victoire', died: 'Mort', cleared: 'Salle' };

type Data = { status: 'loading' } | { status: 'error' } | { status: 'ready'; rows: LeaderboardRow[]; pinned: LeaderboardRow | null; me: string | null };

/** Durée `mm:ss` (les minutes ne sont pas plafonnées à 59). */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function errorMessage(e: unknown): string {
  if (e instanceof Error && e.message) return e.message;
  const m = (e as { message?: unknown } | null)?.message;
  return typeof m === 'string' && m ? m : 'Enregistrement impossible pour le moment.';
}

interface Props { api: LeaderboardApi | null; pseudo: string; onPseudo: (p: string) => void; onDaily: (daily: DailyChoice) => void; onBack: () => void }

/** Page « Classements » du hub : onglets Aujourd'hui / Global, ligne du joueur épinglée, pseudo modifiable et Songe du jour. */
export function Leaderboard({ api, pseudo, onPseudo, onDaily, onBack }: Props) {
  const panel = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const day = useMemo(() => todayIso(), []);
  const [tab, setTab] = useState<Tab>('daily');
  const [data, setData] = useState<Data>({ status: 'loading' });
  const [draft, setDraft] = useState(pseudo);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEscape(onBack);
  useInitialFocus(panel, '.leaderboard-back');

  useEffect(() => { setDraft(pseudo); }, [pseudo]);

  // Rechargé à chaque ouverture d'onglet (pas de cache) ; la réponse tardive d'un onglet quitté est ignorée.
  useEffect(() => {
    if (!api) return;
    let live = true;
    setData({ status: 'loading' });
    Promise.all([tab === 'daily' ? api.daily(day) : api.global(), api.mine(day)]).then(
      ([list, mine]) => {
        if (!live) return;
        const own = (tab === 'daily' ? mine.daily : mine.global) ?? null;
        const { rows, pinned } = pinMine(list, own);
        setData({ status: 'ready', rows, pinned, me: own?.userId ?? null });
      },
      () => { if (live) setData({ status: 'error' }); },
    );
    return () => { live = false; };
  }, [api, tab, day]);

  const onTabKey = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const to = TABS[(TABS.findIndex((t) => t.id === tab) + (e.key === 'ArrowRight' ? 1 : -1) + TABS.length) % TABS.length]!;
    setTab(to.id);
    requestAnimationFrame(() => panel.current?.querySelector<HTMLElement>(`[role="tab"][data-tab="${to.id}"]`)?.focus());
  };

  const submit = (e: Event) => {
    e.preventDefault();
    if (!api || busy) return;
    const value = input.current?.value ?? draft;
    const invalid = validatePseudo(value);
    if (invalid) { setError(invalid); return; }
    setBusy(true);
    api.setPseudo(value).then(
      (saved) => { setBusy(false); setError(''); onPseudo(typeof saved === 'string' && saved !== '' ? saved : value.trim()); },
      (err: unknown) => { setBusy(false); setError(errorMessage(err)); },
    );
  };

  return (
    <div class="panel leaderboard-panel" role="dialog" aria-label="Classements" ref={panel}>
      <div class="leaderboard-head">
        <button class="leaderboard-back" type="button" onClick={onBack}>Retour</button>
        <h2>Classements</h2>
        <div class="leaderboard-tabs" role="tablist" aria-label="Classement affiché" onKeyDown={onTabKey}>
          {TABS.map((t) => (
            <button key={t.id} type="button" role="tab" data-tab={t.id} aria-selected={tab === t.id} tabIndex={tab === t.id ? 0 : -1} class={tab === t.id ? 'is-active' : ''} onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </div>
        <DailyButton api={api} className="leaderboard-daily" onDaily={onDaily} />
      </div>
      <div class="leaderboard-body" role="tabpanel">
        {!api && <p class="leaderboard-offline">Classements indisponibles hors ligne.</p>}
        {api && data.status === 'loading' && <p class="leaderboard-loading" role="status">Chargement...</p>}
        {api && data.status === 'error' && <p class="leaderboard-error" role="alert">Classements indisponibles pour le moment.</p>}
        {api && data.status === 'ready' && (data.rows.length === 0 && !data.pinned
          ? <p class="leaderboard-empty">Aucun score pour le moment.</p>
          : (
            <table class="leaderboard-table">
              <caption>{tab === 'daily' ? 'Classement du jour' : 'Classement global'}</caption>
              <thead>
                <tr><th scope="col">Rang</th><th scope="col">Pseudo</th><th scope="col">Score</th><th scope="col">Salles</th><th scope="col" class="col-duree">Durée</th><th scope="col" class="col-resultat">Résultat</th></tr>
              </thead>
              <tbody>
                {data.rows.map((r) => <Line key={r.userId} row={r} me={r.userId === data.me} />)}
                {data.pinned && <Line row={data.pinned} me pinned />}
              </tbody>
            </table>
          ))}
      </div>
      <form class="pseudo-form" onSubmit={submit}>
        <label for="pseudo-input">Pseudo</label>
        <input id="pseudo-input" ref={input} type="text" maxLength={16} autocomplete="off" spellcheck={false} value={draft} disabled={!api || busy} onInput={(e) => setDraft((e.target as HTMLInputElement).value)} />
        <button type="submit" disabled={!api || busy}>Enregistrer</button>
        {!api && <span class="pseudo-hint">Pseudo modifiable en ligne</span>}
        <p class="pseudo-error" role="alert">{error}</p>
      </form>
    </div>
  );
}

function Line({ row, me, pinned }: { row: LeaderboardRow; me: boolean; pinned?: boolean }) {
  return (
    <tr class={`${me ? 'is-me' : ''}${pinned ? ' pinned' : ''}`.trim() || undefined}>
      {pinned
        ? <td class="rank-you" colSpan={2}><b>Vous : rang {row.rank}</b></td>
        : <><td class="rank">{row.rank}</td><td class="pseudo">{row.pseudo}</td></>}
      <td class="score">{row.score}</td>
      <td class="rooms">{row.roomsCleared}</td>
      <td class="col-duree">{formatDuration(row.durationMs)}</td>
      <td class="col-resultat">{OUTCOME[row.outcome] ?? row.outcome}</td>
    </tr>
  );
}
